import React, { useMemo, useCallback } from "react";
import { useOrg } from "@/hooks/useOrg";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { CartesianGrid, LineChart, Line, XAxis, YAxis, Legend } from "recharts";
import { Skeleton } from "../ui";
import { CircleAlert, ShareIcon, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/Card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/Chart";
import { useTranslation } from "react-i18next";
import { useCommunityGrowthSeries } from "./hooks/useCommunityGrowthSeries";
import {
  ChartDataPoint,
  combineData,
  buildDateRangeMap,
} from "@/util/chartUtils";
import { ChartHeader } from "../ChartHeader";
import { useShareLink } from "@/hooks/useShareLink";
import { Legend as CustomLegend } from "./Legend";
import { getDateString } from "@/util/dateUtils";
import { standardFormatDateShort } from "@/util/stringUtils";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";
import { buildConfig } from "./config";

// what day of the month the XAxis tick should line up with
const MONTH_TICK_DAY = 15;

export const CommunityGrowthChart = () => {
  const { org } = useOrg();
  const { cohortYear } = useCurrentCohortYear();
  const { t } = useTranslation();

  const startDate = useMemo(
    () => (cohortYear ? new Date(cohortYear - 1, 6, 1) : new Date()),
    [cohortYear],
  );

  const endDate = useMemo(
    () => (cohortYear ? new Date(cohortYear, 8, 1) : new Date()),
    [cohortYear],
  );

  const { data, errors, loading } = useCommunityGrowthSeries(
    cohortYear,
    org?.id,
    startDate,
    endDate,
  );

  const chartData = useMemo(() => {
    if (data && cohortYear) {
      const range = buildDateRangeMap(
        getDateString(startDate),
        getDateString(endDate),
      );
      return combineData(
        range,
        Object.values(data).filter((data) => data !== undefined),
      );
    }
  }, [data, cohortYear, startDate, endDate]);

  const chartConfig = buildConfig(data);

  if (errors && errors.length > 0) {
    return (
      <Card>
        <div className="flex h-[282px] w-full flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <CircleAlert className="h-12 w-12 text-muted-foreground" />
            <div className="text-center text-sm text-muted-foreground">
              {t("adoption.communityGrowthChart.loadingError")}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <GrowthLineChart
      chartData={chartData || []}
      chartConfig={chartConfig}
      loading={loading}
    />
  );
};

const getTicks = (chartData: Record<string, string | number>[]): string[] => {
  const ticks: string[] = [];
  for (const dataPoint of chartData) {
    const date = dataPoint["date"] as string;
    if (date) {
      if (new Date(date).getDate() === MONTH_TICK_DAY) {
        ticks.push(date);
      }
    }
  }
  return ticks;
};

export function GrowthLineChart({
  chartData,
  chartConfig,
  loading,
}: {
  chartData: ChartDataPoint[];
  chartConfig: ChartConfig;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const { shareCurrentLink } = useShareLink();

  const handleExportCSV = useCallback(() => {
    if (!chartData || chartData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    try {
      const cohortYears = Object.keys(chartConfig)
        .filter((key) => !chartConfig[key].hide)
        .sort((a, b) => parseInt(b) - parseInt(a));

      const headers = ["", ...cohortYears];

      const exportData = chartData.map((dataPoint) => {
        const row: Record<string, string | number> = {
          "": new Date(
            (dataPoint.date as string) + "T00:00:00",
          ).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
        };

        cohortYears.forEach((year) => {
          const value = (dataPoint as Record<string, unknown>)[year];
          row[year] = (typeof value === "number" ? value : "") || "";
        });

        return row;
      });

      exportChartCSV({
        filename: timestampFilename("community-growth-chart"),
        headers,
        data: exportData,
      });

      toast.success(t("global.exportToCSV.success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  }, [chartData, chartConfig, t]);

  return (
    <Card>
      <ChartHeader
        title={t("adoption.communityGrowthChart.title")}
        subtitle={t("adoption.communityGrowthChart.description")}
        menuOptions={[
          {
            label: t("global.exportToCSV.menuTitle"),
            onClick: handleExportCSV,
            icon: <FileDown aria-hidden height={16} />,
          },
          {
            label: t("adoption.communityGrowthChart.menuOptions.share"),
            onClick: shareCurrentLink,
            icon: <ShareIcon aria-hidden height={16} />,
          },
        ]}
      ></ChartHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[305px] w-full" />
        ) : (
          <ChartContainer className="h-[305px] w-full" config={chartConfig}>
            <LineChart accessibilityLayer data={chartData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                ticks={getTicks(chartData)}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                  });
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={50}
                tickFormatter={(value) => {
                  return new Intl.NumberFormat().format(value);
                }}
              />
              <ChartTooltip
                cursor={false}
                labelFormatter={(label) => (
                  <div>
                    <div>{standardFormatDateShort(label)}</div>
                    <div className="text-muted-foreground">Cohort</div>
                  </div>
                )}
                content={
                  <ChartTooltipContent
                    indicator={"line"}
                    nestLabelOverride={false}
                  />
                }
              />
              <Legend
                verticalAlign="top"
                align="right"
                content={(props) => (
                  <CustomLegend chart={props} config={chartConfig} />
                )}
              />
              {Object.entries(chartConfig).map(([dataKey, line]) => {
                return (
                  <Line
                    key={dataKey}
                    dataKey={dataKey}
                    type="linear"
                    stroke={line.color}
                    strokeWidth={2}
                    dot={false}
                  />
                );
              })}
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
